import { z } from "zod";
import { isPublicMailDomain } from "./mailHost";

/** M7 round seven: the own sending domain through SES (brief `docs/briefs/m7-billing.md`, round seven; the Mail card's second way,
 *  "Your own domain, with DNS records"). The shapes the API answers and accepts, and the one rule the `check` route and the hourly
 *  job share. The two mail rules (`dnsRecordsFor`, `fromAddressFor`) live under `@omnabase/shared/mail` with the other addresses. */

export const SENDING_DOMAIN_STATUSES = ["pending", "verified", "failed", "paused"] as const;
export type SendingDomainStatus = (typeof SENDING_DOMAIN_STATUSES)[number];
/** why a domain is paused: the plan lost `ownDomains` (a person's check or, since 2026-10-27, a move back into a plan with own domains lifts
 *  it), or SES saw its DKIM records break */
export const SENDING_DOMAIN_PAUSED_REASONS = ["plan", "dkim"] as const;
export type SendingDomainPausedReason = (typeof SENDING_DOMAIN_PAUSED_REASONS)[number];
/** how long a new domain waits for its records before it is marked failed (the owner is told once) */
export const SENDING_DOMAIN_PENDING_DAYS = 7;

/** our own identity: a customer may never register it or a name under it (they would send as us) */
const OUR_DOMAIN = "omnabase.com";
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
/** the refusal for ours and for a public mail provider (the first setup round, 2026-10-22: one sentence for the field and the API) */
export const SENDING_DOMAIN_REFUSED = "Replies need a domain you can add DNS records to.";
const isOurs = (domain: string) => domain === OUR_DOMAIN || domain.endsWith(`.${OUR_DOMAIN}`);
/** a host name of two or more labels (1–63 letters, digits, inner hyphens), never an IP address (the last label carries a letter); the
 *  caller has trimmed and lower-cased it */
function isHostName(domain: string): boolean {
  if (domain.length === 0 || domain.length > 253) return false;
  const labels = domain.split(".");
  if (labels.length < 2 || !labels.every((l) => LABEL.test(l))) return false;
  return /[a-z]/.test(labels[labels.length - 1]!);
}
/** a domain the customer can add DNS records to: a host name, never ours, never a public mail provider (gmail.com, gmx.fr …) */
export const isOwnableDomain = (domain: string): boolean => !isOurs(domain) && !isPublicMailDomain(domain);
/** a host name that may become a sending domain */
export const isSendingDomain = (domain: string): boolean => isHostName(domain) && isOwnableDomain(domain);

/** the part before the @ on the own domain: lower-cased; letters, digits, dots, hyphens, underscores; no dot at either end or twice;
 *  no `+` (our routing reads it as a tag) */
export const SendingLocal = z.string().trim().toLowerCase().min(1).max(64).regex(/^[a-z0-9_-]+(?:\.[a-z0-9_-]+)*$/, { message: "Use letters, digits, dots, hyphens and underscores" });

/** POST /sending-domains: the domain, trimmed and lower-cased, and (the first setup round) the project it is for and the part before the @
 *  the person typed; refused before anything reaches Amazon */
export const SendingDomainCreateSchema = z.object({
  domain: z.string().trim().toLowerCase()
    .refine(isHostName, { message: "Enter a domain such as support.example.com (not an address, a link or one of ours)", abort: true })
    .refine(isOwnableDomain, { message: SENDING_DOMAIN_REFUSED }),
  projectId: z.string().min(1).optional(),
  sendingLocal: SendingLocal.optional(),
}).strict();


export const DnsRecordSchema = z.object({ type: z.enum(["CNAME", "TXT"]), name: z.string(), value: z.string(), optional: z.boolean() });
export type DnsRecord = z.infer<typeof DnsRecordSchema>;
/** a sending domain as the API answers it; `records` are built from SES's DKIM tokens (the three CNAMEs, then the DMARC suggestion) */
export const SendingDomainSchema = z.object({
  id: z.string(), domain: z.string(), status: z.enum(SENDING_DOMAIN_STATUSES), pausedReason: z.enum(SENDING_DOMAIN_PAUSED_REASONS).nullable(),
  records: z.array(DnsRecordSchema), verifiedAt: z.string().nullable(), lastCheckedAt: z.string().nullable(), createdAt: z.string(),
  /** the first setup round: who runs the domain's name servers, looked up once when it was added ("Cloudflare", "all-inkl", or null) */
  nameServers: z.object({ provider: z.string().nullable() }).optional(),
  /** the project that added it (it switches to the domain when the domain verifies), or null */
  projectId: z.string().nullable().optional(),
});
export type SendingDomain = z.infer<typeof SendingDomainSchema>;
export const SendingDomainListSchema = z.object({ rows: z.array(SendingDomainSchema) });

/* ---------- the one rule: what a check of SES's answer does to a row ---------- */

/** SES's `GetEmailIdentity`: `DkimAttributes.Status` and `VerifiedForSendingStatus`; null when SES does not know the identity */
export type SesDkimStatus = "PENDING" | "SUCCESS" | "FAILED" | "TEMPORARY_FAILURE" | "NOT_STARTED";
export type SesIdentityAnswer = { dkimStatus: SesDkimStatus; verifiedForSending: boolean } | null;
/** `verifiedAt` (the mail logo and domain pause round, 2026-10-27): when the domain was first verified; null or absent: never. A plan pause
 *  over a domain that worked before is read as a working domain, so a lift never fails it for good. */
export type DomainRowState = { status: SendingDomainStatus; pausedReason: SendingDomainPausedReason | null; createdAt: string; verifiedAt?: string | null };
/** who checks: the hourly job, a person's (or the back office's) "Check now", or the lift after a plan move into own domains */
export type DomainCheckOptions = { now: string; planAllows: boolean; by: "job" | "check" | "plan" };
export type NextDomainState = { status: SendingDomainStatus; pausedReason: SendingDomainPausedReason | null; tell: "failed" | "paused" | "verified" | null };

const DAY_MS = 86_400_000;
const state = (status: SendingDomainStatus, pausedReason: SendingDomainPausedReason | null = null): Omit<NextDomainState, "tell"> => ({ status, pausedReason });

/** The state a domain moves to after SES answered (worker test's six rules): the plan first (a plan without `ownDomains` pauses, a
 *  failed row stays failed), the job leaves plan pauses and failed rows alone, SES's success verifies, a break pauses a working domain
 *  (`dkim`) and fails any other, a waiting domain fails after seven days. `tell` names a move INTO failed or a dkim pause made by the
 *  job, so the owners are mailed once; and (the first setup round, 2026-10-22) a domain's first verification, by the job or a person's
 *  check, from pending or failed (a domain back from a dkim pause worked before; the database keeps the told-once mark). The mail logo and
 *  domain pause round (2026-10-27): a plan pause over a domain that was verified before counts as working (broken or unknown: a dkim pause,
 *  which the job keeps watching, never failed), and one over a domain never verified is new, so its verification is told. */
export function nextDomainState(row: DomainRowState, ses: SesIdentityAnswer, o: DomainCheckOptions): NextDomainState {
  const next = moveOf(row, ses, o);
  const intoFailed = next.status === "failed" && row.status !== "failed";
  const intoDkim = next.status === "paused" && next.pausedReason === "dkim" && !(row.status === "paused" && row.pausedReason === "dkim");
  const neverWorked = row.status === "pending" || row.status === "failed" || (isPlanPaused(row) && !row.verifiedAt);
  if (next.status === "verified" && neverWorked) return { ...next, tell: "verified" };
  return { ...next, tell: o.by === "job" && (intoFailed || intoDkim) ? (intoFailed ? "failed" : "paused") : null };
}
const isPlanPaused = (row: DomainRowState) => row.status === "paused" && row.pausedReason === "plan";
/** a domain that sent before: verified, paused for its DKIM records, or paused for the plan after it had been verified */
const workedBefore = (row: DomainRowState) => row.status === "verified" || (row.status === "paused" && row.pausedReason === "dkim") || (isPlanPaused(row) && !!row.verifiedAt);
function moveOf(row: DomainRowState, ses: SesIdentityAnswer, o: DomainCheckOptions): Omit<NextDomainState, "tell"> {
  if (!o.planAllows) return row.status === "failed" ? state("failed") : state("paused", "plan");
  if (o.by === "job" && (isPlanPaused(row) || row.status === "failed")) return state(row.status, row.pausedReason);
  if (ses?.dkimStatus === "SUCCESS" && ses.verifiedForSending) return state("verified");
  if (workedBefore(row)) return !ses || ses.dkimStatus === "FAILED" || !ses.verifiedForSending || isPlanPaused(row) ? state("paused", "dkim") : state(row.status, row.pausedReason);
  if (!ses || ses.dkimStatus === "FAILED") return state("failed");
  const old = new Date(o.now).getTime() - new Date(row.createdAt).getTime() >= SENDING_DOMAIN_PENDING_DAYS * DAY_MS;
  return old ? state("failed") : state("pending");
}

/* ---------- the first setup round (2026-10-22): the sending address, its prefill, and the name servers ---------- */

/** The "Replies come from" field's offer: the receiving step's support address, trimmed and lower-cased, when it is on a domain of the
 *  customer's own; null for ours, a public mail provider, or no address. */
export function sendingAddressPrefill(supportAddress: string | null | undefined): string | null {
  const a = (supportAddress ?? "").trim().toLowerCase();
  const parsed = parseSendingAddress(a);
  return a.includes("@") && parsed.ok && parsed.address === a ? a : null;
}

export type ParsedSendingAddress = { ok: true; address: string; local: string; domain: string } | { ok: false; problem: string };
/** What the person typed into "Replies come from": a whole address, or a bare domain completed with "support@". The domain gets the
 *  records; the part before the @ follows the sending-address rules (no "+"). */
export function parseSendingAddress(typed: string): ParsedSendingAddress {
  const t = typed.trim().toLowerCase();
  const at = t.lastIndexOf("@");
  const local = at < 0 ? "support" : t.slice(0, at), domain = at < 0 ? t : t.slice(at + 1);
  if (!isHostName(domain)) return { ok: false, problem: "Enter an address such as support@example.com." };
  if (!isOwnableDomain(domain)) return { ok: false, problem: SENDING_DOMAIN_REFUSED };
  if (!SendingLocal.safeParse(local).success) return { ok: false, problem: "Use letters, digits, dots, hyphens and underscores before the @." };
  return { ok: true, address: `${local}@${domain}`, local, domain };
}

/** the name-server suffixes we can name (the DNS help says "Your name servers: <provider>") */
const NAME_SERVER_PROVIDERS: [string, string][] = [["ns.cloudflare.com", "Cloudflare"], ["kasserver.com", "all-inkl"]];
/** the provider's display name for name servers we recognise, else null */
export function nameServerProvider(names: readonly string[]): string | null {
  for (const raw of names) {
    const n = raw.trim().toLowerCase().replace(/\.$/, "");
    for (const [suffix, provider] of NAME_SERVER_PROVIDERS) if (n === suffix || n.endsWith(`.${suffix}`)) return provider;
  }
  return null;
}
